// src/components/Footer.jsx
import React from "react";
import { Box, Container, Button, Typography, Stack, Divider } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { keyframes } from "@emotion/react";

import image1 from "../images/Recurso.svg";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const goHomeAndScroll = (id) => {
    if (location.pathname === "/") {
      scrollToId(id);
      return;
    }
    navigate("/");
    setTimeout(() => scrollToId(id), 80);
  };

  // Links en columna, compactos, con subrayado delicado
  const linkSx = {
    justifyContent: "flex-start",
    textTransform: "none",
    fontWeight: 500,
    fontSize: { xs: "0.98rem", sm: "1rem" },
    color: "inherit",
    px: 0,
    py: 0.18,
    minWidth: "auto",
    borderRadius: 0,
    position: "relative",
    "&:hover": { backgroundColor: "transparent", transform: "translateY(-1px)" },
    transition: "transform 220ms ease",
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      bottom: -5,
      height: 2,
      width: "62%",
      backgroundColor: "currentColor",
      transform: "scaleX(0)",
      transformOrigin: "left",
      transition: "transform 240ms ease",
      opacity: 0.75,
    },
    "&:hover::after": { transform: "scaleX(1)" },
  };

  const gradientShift = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  `;

  // Botón Reservar (mismo del navbar)
  const reserveSx = {
    width: "100%",
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 700,
    color: "#fff",
    py: 1.1,
    boxShadow: "0 10px 22px rgba(0,0,0,0.18)",
    background: "linear-gradient(270deg, #2DB193, #16a357, #c72e64)",
    backgroundSize: "600% 600%",
    animation: `${gradientShift} 3.2s ease infinite`,
    transition: "transform 220ms ease, box-shadow 220ms ease, filter 220ms ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 14px 30px rgba(0,0,0,0.22)",
      filter: "brightness(1.05)",
    },
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "primary.main",
        color: "#1b1b1b",
        borderTop: "1px solid rgba(0,0,0,0.08)",
      }}
    >
      <Container sx={{ py: { xs: 3, md: 3.2 } }}>
        {/* BLOQUE CENTRAL: 2 columnas juntas + divider vertical, centradas */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems="stretch"
            justifyContent="center"
            spacing={{ xs: 2.2, md: 3.2 }} // 👈 juntas
            sx={{
              width: "fit-content",
              maxWidth: "100%",
            }}
          >
            {/* Columna 1: Logo + reservar */}
            <Box
              sx={{
                width: { xs: "100%", md: 320 },
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "flex-start", md: "flex-start" },
              }}
            >
              <Box
                onClick={() => navigate("/")}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <img
                  src={image1}
                  alt="Bichón"
                  style={{ height: 100, objectFit: "contain" }} // 👈 más grande
                />
              </Box>

              <Typography sx={{ mt: 0.4, fontSize: 13.5, opacity: 0.8 }}>
                La Casa Hostal — Pichilemu
              </Typography>

              <Box sx={{ mt: 1.4, width: "100%" }}>
                <Button
                  variant="contained"
                  sx={reserveSx}
                  onClick={() => navigate("/reserve")}
                >
                  Reservar ahora
                </Button>
              </Box>
            </Box>

            {/* Divider vertical SOLO en desktop */}
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: "none", md: "block" },
                borderColor: "rgba(0,0,0,0.10)",
              }}
            />

            {/* Columna 2: Navegación */}
            <Box
              sx={{
                width: { xs: "100%", md: 240 },
                pt: { xs: 0, md: 1 }, // para alinear visualmente con logo
              }}
            >
              <Typography sx={{ fontWeight: 800, mb: 0.9 }}>
                Navegación
              </Typography>

              <Stack spacing={0.55}>
                <Button sx={linkSx} onClick={() => goHomeAndScroll("instalaciones")}>
                  Instalaciones
                </Button>
                <Button sx={linkSx} onClick={() => goHomeAndScroll("ubicacion")}>
                  Ubicación
                </Button>
                <Button sx={linkSx} onClick={() => goHomeAndScroll("experiencias")}>
                  Experiencias
                </Button>
                <Button sx={linkSx} onClick={() => goHomeAndScroll("contactanos")}>
                  Contáctanos
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Box>

        {/* Divider horizontal */}
        <Divider
          sx={{
            my: { xs: 2, md: 2.2 },
            borderColor: "rgba(0,0,0,0.08)",
          }}
        />

        {/* BOTTOM BAR */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 720,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 0.4,
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
            }}
          >
            <Typography sx={{ fontSize: 13, opacity: 0.8 }}>
              © {new Date().getFullYear()} Hostel Bichón
            </Typography>
            <Typography sx={{ fontSize: 13, opacity: 0.7 }}>
              Pichilemu, Chile
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
